import { useEffect, useState } from 'react';
import {
  AgxBodyText,
  AgxColumn,
  AgxLabel,
  AgxRadio,
  AgxRow,
  AgxSelect,
  Images,
  Option,
} from '@urbanx/agx-ui-components';
import {
  AgxTextInput,
} from '@urbanx/agx.ui-components2'
import { useAppSelector } from 'hooks/useAppSelector';
import { CommissionSplit } from './types';
import './CommissionSplit.scss';

interface CommissionSplitProps {
  id: string;
  label: string;
  defaultValue?: CommissionSplit;
  validate?: boolean;
  onValueChanged: (value: { id: string; value: CommissionSplit }) => void;
}

const CommissionSplitComponent = ({
  id,
  label,
  defaultValue = {},
  validate,
  onValueChanged,
}: CommissionSplitProps) => {
  const { splittingWithSecondaryAgent, leadAgentSplit, secondAgentSplit } =
    defaultValue;

  const { agents } = useAppSelector(state => state.listingAgent);

  const agentOptions = [
    {
      value: '',
      label: 'Please select an agent',
    },
  ].concat(
    agents.map(agent => {
      return {
        value: agent.id,
        label: `${agent.name?.firstName} ${agent.name?.lastName}`,
      };
    })
  );

  const [secondaryAgentOptions, setSecondaryAgentOptions] = useState<Option[]>([
    {
      value: '',
      label: 'Please select an agent',
    },
  ]);
  const [leadAgentOptions, setLeadAgentOptions] = useState([...agentOptions]);

  const [localError, setLocalError] = useState('');
  const [totalPercentage, setTotalPercentage] = useState<number | undefined>(
    undefined
  );
  const [commissionSplit, setCommissionSplit] = useState({
    splittingWithSecondaryAgent: splittingWithSecondaryAgent ?? false,
    leadAgentSplit: leadAgentSplit ?? {
      agentId: '',
      percentage: '',
    },
    secondAgentSplit: secondAgentSplit ?? {
      agentId: '',
      percentage: '',
    },
  });

  const updateLocalError = (commissionSplit: CommissionSplit) => {
    const { leadAgentSplit, secondAgentSplit } = commissionSplit;
    const commissionPercentageTotal =
      Number(leadAgentSplit?.percentage || 0) +
      Number(secondAgentSplit?.percentage || 0);

    setTotalPercentage(commissionPercentageTotal);

    if (
      (!leadAgentSplit?.agentId && !leadAgentSplit?.percentage) ||
      (!secondAgentSplit?.agentId && !secondAgentSplit?.percentage)
    ) {
      setLocalError('Select agent and commission');
    } else if (!leadAgentSplit.agentId || !secondAgentSplit.agentId) {
      setLocalError('Select agent');
    } else if (!leadAgentSplit.percentage || !secondAgentSplit.percentage) {
      setLocalError('Enter commission');
    } else if (commissionPercentageTotal !== 100) {
      setLocalError('Total commission percentage should be exactly 100%');
    } else {
      setLocalError('');
    }
  };

  useEffect(() => {
    onValueChanged({
      id,
      value: {
        ...commissionSplit,
      },
    });
  }, [commissionSplit]);

  return (
    <div className="agxRadio">
      <AgxLabel medium>{label}</AgxLabel>
      <div className="agxRadio-group">
        <AgxRadio
          id={'noCommisionSplit'}
          label={`No`}
          onCheckChanged={() =>
            setCommissionSplit({
              ...commissionSplit,
              splittingWithSecondaryAgent: false,
            })
          }
          checked={commissionSplit.splittingWithSecondaryAgent === false}
        />
        <AgxRadio
          id={`yesCommisionSplit`}
          label={`Yes`}
          onCheckChanged={() => {
            setCommissionSplit({
              ...commissionSplit,
              splittingWithSecondaryAgent: true,
            });
          }}
          checked={commissionSplit.splittingWithSecondaryAgent === true}
          subContent={
            commissionSplit.splittingWithSecondaryAgent === true
              ? [
                <AgxColumn largeGap fill>
                  <AgxRow centered mediumGap fill>
                    <AgxSelect
                      id={`leadAgent`}
                      key={`leadAgent`}
                      label="Lead Agent"
                      options={leadAgentOptions}
                      error={
                        validate && !commissionSplit.leadAgentSplit.agentId
                          ? localError
                          : undefined
                      }
                      hideErrorDescription
                      onValueChanged={({ value }) => {
                        const obj = {
                          ...commissionSplit,
                          leadAgentSplit: {
                            ...commissionSplit.leadAgentSplit,
                            agentId: value,
                          },
                        };
                        setCommissionSplit(obj);
                        setSecondaryAgentOptions(
                          agentOptions.filter(agent => agent.value !== value)
                        );
                        updateLocalError(obj);
                      }}
                      dataTestId={`agx-commissionSplit-leadAgent`}
                      required
                      defaultValue={
                        agentOptions.find(
                          agent =>
                            agent.value ===
                            commissionSplit?.leadAgentSplit?.agentId
                        ) ?? agentOptions[0]
                      }
                    />
                    <div className="commissionPercentage">
                      <AgxTextInput
                        id="leadAgentPercent"
                        noHeader
                        optionalText=''
                        type='percent'
                        onInputValueChange={({ value }) => {
                          const obj = {
                            ...commissionSplit,
                            leadAgentSplit: {
                              ...commissionSplit.leadAgentSplit,
                              percentage: value,
                            },
                          };
                          setCommissionSplit(obj);
                          updateLocalError(obj);
                        }}
                        defaultValue={commissionSplit.leadAgentSplit.percentage}

                        errorText={
                          validate &&
                            (!commissionSplit.leadAgentSplit.percentage ||
                              totalPercentage !== 100)
                            ? localError
                            : ''
                        }
                        hideErrorDescription
                      />
                    </div>
                  </AgxRow>
                  <AgxRow centered mediumGap>
                    <AgxSelect
                      id={`secondAgent`}
                      key={`secondAgent`}
                      label="Second Agent"
                      options={secondaryAgentOptions}
                      error={
                        validate && !commissionSplit.secondAgentSplit.agentId
                          ? localError
                          : undefined
                      }
                      hideErrorDescription
                      onValueChanged={({ value }) => {
                        const obj = {
                          ...commissionSplit,
                          secondAgentSplit: {
                            ...commissionSplit.secondAgentSplit,
                            agentId: value,
                          },
                        };
                        setCommissionSplit(obj);
                        setLeadAgentOptions(
                          agentOptions.filter(agent => agent.value !== value)
                        );
                        updateLocalError(obj);
                      }}
                      dataTestId={`agx-commissionSplit-secondAgent`}
                      required
                      defaultValue={
                        agentOptions.find(
                          agent =>
                            agent.value ===
                            commissionSplit?.secondAgentSplit?.agentId
                        ) ?? agentOptions[0]
                      }
                    />
                    <div className="commissionPercentage">

                      <AgxTextInput
                        id="secondAgentPercent"
                        type='percent'
                        optionalText=''
                        defaultValue={
                          commissionSplit.secondAgentSplit.percentage
                        }
                        onInputValueChange={({ value }) => {
                          const obj = {
                            ...commissionSplit,
                            secondAgentSplit: {
                              ...commissionSplit.secondAgentSplit,
                              percentage: value,
                            },
                          };
                          setCommissionSplit(obj);
                          updateLocalError(obj);
                        }}
                        errorText={
                          validate &&
                            (!commissionSplit.secondAgentSplit.percentage ||
                              totalPercentage !== 100)
                            ? localError
                            : ''
                        }
                        hideErrorDescription
                      />
                    </div>
                  </AgxRow>
                  {validate && localError && (
                    <AgxRow mediumGap>
                      <Images.AlertCircle />
                      <AgxBodyText small extraClasses={'errorMessage'}>
                        {localError}
                      </AgxBodyText>
                    </AgxRow>
                  )}
                </AgxColumn>,
              ]
              : undefined
          }
        />
      </div>
    </div>
  );
};

export default CommissionSplitComponent;
